// PROTOCOL.md (version 1, revision 1.8) as TypeScript types. The Rust types in
// crates/protocol are normative for field names; this mirrors them for UIs.

export const PROTOCOL_VERSION = 1;
export const MIC_SAMPLE_RATE = 16000;
export const TTS_SAMPLE_RATE = 24000;
/** Bytes of `reply_id` before the PCM in every down audio frame. */
export const AUDIO_HEADER_BYTES = 8;

/** Turn, reply and session ids: unique for the life of one kit process (see `epoch`). */
export type Id = number;
export type KitState = "idle" | "listening" | "thinking" | "speaking";
export type SessionAudio = "none" | "client" | "local";
export type IgnoredReason = "noise" | "unsure" | "filler" | "other_speaker";
export type HeardOutcome = "finished" | "silenced" | "unspoken";
export type ReplyOutcome = "completed" | "failed" | "cancelled";
/** `[text (with trailing space), start_ms, end_ms]`, relative to the chunk. */
export type Word = [string, number, number];
/**
 * Optional features (1.3, PROTOCOL.md section 2.1), declared in `hello.features`. A client that
 * declares none sees exactly the v1 wire.
 */
export type Feature =
  | "conversations"
  | "display_text"
  | "priority_now"
  | "dictation"
  | "attachments"
  | "speaker_sources"
  | "speaker_spans"
  | "guests"
  /** 1.9: `reply.pause`, `reply.resume` and the `reply.paused` event. */
  | "pause"
  /** 1.10: "That was me": `ignored_id`s, `speech.mine`, `speech.reattributed`, `speech.marks`. */
  | "speech_mine"
  /** 1.11, phones: `conversation.recent` after `welcome`, what a phone missed while away. */
  | "conversation_recent";
/** 1.4: what a session does with the mic's speech. */
export type SessionMode = "conversation" | "dictation";
/** 1.4: how a dictation session cuts utterances: hands-free (`endpoint`) or `push_to_talk`. */
export type DictationTurns = "endpoint" | "push_to_talk";

/** 1.5, feature `attachments`: a file sent with a turn, as a transcript shows it. */
export interface AttachmentInfo {
  id: string;
  name: string;
  mime: string;
  size: number;
  /** A small `data:image/...` preview, if the sender made one. */
  thumb?: string;
}

/**
 * 1.7, `speaker_spans`: one stretch of an utterance that "only listen to me" split by voice. `mine`:
 * the user's words; otherwise speech that was ignored (never sent anywhere). Times are ms from the
 * start of the utterance.
 */
export interface SpeakerSpan {
  text: string;
  start_ms: number;
  end_ms: number;
  mine: boolean;
  score: number;
  /** 1.8, `guests`: the guest this other voice's span was grouped into. */
  guest_id?: string;
  /** 1.10, `speech_mine`: the ignored speech this other voice's span is part of. */
  ignored_id?: string;
}

/**
 * 1.10, `speech_mine`: one "That was me" in the kit's history (the last 200). `learned`: added
 * to the user's voiceprint for that mic; otherwise `reason` says why not (`below_floor`: it
 * scored `score`, under `floor`, too different from their voice to learn from).
 */
export interface MarkInfo {
  mark_id: string;
  /** Unix seconds. */
  at: number;
  profile?: string;
  source?: string;
  score: number;
  threshold: number;
  floor: number;
  learned: boolean;
  /** Open: learned, below_floor, two_voices, no_voice, mic_not_enrolled, unavailable, store_failed, undone. */
  reason: string;
  /** The guest it had been put with, and their match. */
  guest?: string;
  guest_score?: number;
  /** What was said (only when the kit shows ignored speech). */
  text?: string;
}

/**
 * 1.8, `guests`: another voice heard this session ("only listen to me" groups what it ignores by
 * voice). Session guests are forgotten when the session ends; `saved` ones (named, and
 * remembered with the user's choice) are recognised next time.
 */
export interface GuestInfo {
  guest_id: string;
  /** Their name, else "Guest 1", "Guest 2", ... */
  label: string;
  name?: string;
  saved?: boolean;
  /** Asked to be remembered; saved once enough of their speech has been heard. */
  save_pending?: boolean;
  speech_ms: number;
}

export interface SpeakerMatch {
  score: number;
  threshold: number;
  matched: boolean;
  profile?: string;
  short?: boolean;
  /** 1.6, `speaker_sources`: which of the profile's mics matched best. */
  source?: string;
  /**
   * Why a non-match was rejected (kit 0.9; absent on a match): `below_threshold`,
   * `guest_closer` (a guest's voice matches better), or `unverified` ("only listen to me" is on
   * but could not check the voice, e.g. no voiceprint yet, so the speech was dropped).
   */
  reason?: string;
}

/**
 * A voice profile. Before 1.6 (and without `speaker_sources`) each mic is its own profile:
 * `pc`, `device:<deviceId>`. With it, the user's profile is `me` and `sources` lists its mics.
 */
export interface SpeakerProfileInfo {
  profile: string;
  speech_s: number;
  created_at: number;
  /** 1.6, `speaker_sources`: the mics this voice was learned on. */
  sources?: SpeakerSourceInfo[];
}

/** 1.6: one mic a profile was learned on (`pc`, `device:<deviceId>`). */
export interface SpeakerSourceInfo {
  source: string;
  speech_s: number;
  created_at: number;
}

/** The user's own profile (1.6); `pc` and `device:<id>` name its mics. */
export const DEFAULT_SPEAKER_PROFILE = "me";

/**
 * The mics the user's voice was learned on, from a `speaker.status` in either form: 1.6
 * (`me` with `sources`) or v1 (one `pc` / `device:<id>` profile per mic, which phones get).
 */
export function voiceSources(profiles: SpeakerProfileInfo[]): SpeakerSourceInfo[] {
  const out: SpeakerSourceInfo[] = [];
  for (const p of profiles) {
    if (p.sources?.length) {
      if (p.profile === DEFAULT_SPEAKER_PROFILE) out.push(...p.sources);
    } else if (p.profile === "pc" || p.profile.startsWith("device:")) {
      out.push({ source: p.profile, speech_s: p.speech_s, created_at: p.created_at });
    }
  }
  return out.sort((a, b) => a.source.localeCompare(b.source));
}

/** A paired phone (PROTOCOL.md section 10). */
export interface PhoneDevice {
  device_id: string;
  name: string;
  /** `pending` (not confirmed yet), `ok` or `revoked`. */
  status: string;
  sas: string;
  /** Unix seconds. */
  created: number;
}

export type ServerMsg =
  | {
      type: "welcome";
      protocol: number;
      server: string;
      epoch: string;
      state: KitState;
      session_id: Id | null;
      brain_attached: boolean;
      audio: { mic_rate: number; tts_rate: number };
      engines?: Record<string, string>;
      /** 1.3: the declared features the kit supports; present only if `hello` had `features`. */
      features?: string[];
    }
  | { type: "state"; state: KitState }
  | { type: "session.started"; session_id: Id; audio: SessionAudio; mode?: SessionMode }
  // dictation (1.4, PROTOCOL.md section 2.2)
  | { type: "dictation.partial"; id: Id; text: string }
  | { type: "dictation.final"; id: Id; text: string }
  | { type: "session.ended"; session_id: Id; reason: string }
  | { type: "vad.speech_start" }
  | { type: "vad.speech_stop" }
  | {
      type: "speech.ignored";
      reason: IgnoredReason;
      speaker?: SpeakerMatch;
      /**
       * What STT heard (1.1). With `other_speaker` this is ignored speech: show it apart from the
       * user's turns (`TranscriptModel` option `showIgnored`), never send or insert it. Absent
       * when the host turned it off (`SpeakerConfig::ignored_text`, PROTOCOL.md section 8).
       */
      text?: string;
      voiced_ms?: number;
      /** 1.7, `speaker_spans`: the utterance split by voice. */
      spans?: SpeakerSpan[];
      /** 1.7: the other voice's part of this turn (without `speaker_spans` only). */
      turn_id?: Id;
      /** 1.8, `guests`: the guest this speech was grouped into. */
      guest_id?: string;
      /** 1.10, `speech_mine`: this speech's id, for `speech.mine` ("That was me"). */
      ignored_id?: string;
    }
  | {
      type: "barge";
      reply_id: Id;
      accepted: boolean;
      /** Open: speech, noise, filler, unsure, empty, other_speaker, stt_failed, stale. */
      reason: string;
      text?: string;
      voiced_ms: number;
      speaker?: SpeakerMatch;
    }
  | {
      type: "turn.timing";
      turn_id: Id;
      reply_id: Id;
      typed?: boolean;
      end_of_turn_ms?: number;
      stt_ms?: number;
      brain_ttft_ms?: number;
      tts_ms?: number;
      first_audio_ms?: number;
    }
  | { type: "stt.partial"; text: string }
  | {
      type: "stt.final";
      turn_id: Id;
      text: string;
      speaker?: SpeakerMatch;
      /** 1.5, `attachments` only: the files that went with this turn. */
      attachments?: AttachmentInfo[];
      /**
       * 1.7, `speaker_spans` only: the utterance split by voice, in order. `text` is the user's
       * spans; the others are ignored speech, to show set apart (never send them).
       */
      spans?: SpeakerSpan[];
      /**
       * 1.10, `speech_mine`: this turn is the ignored speech `ignored_id`, marked as the user's
       * ("That was me"): show it in that speech's place, not as a new line.
       */
      ignored_id?: string;
    }
  | {
      type: "speaker.enroll.status";
      profile: string;
      /** 1.6, `speaker_sources`: the mic being learned. */
      source?: string;
      state: "recording" | "processing" | "saved" | "cancelled" | "failed";
      progress: number;
      prompt?: string;
      message?: string;
    }
  | { type: "speaker.status"; enabled: boolean; available: boolean; profiles: SpeakerProfileInfo[] }
  /**
   * 1.8, `guests`: another voice's speech, grouped into a session guest. An interruption log
   * for a sidebar: never a turn, never sent to the brain. `guest_id` absent: "Someone" (too
   * short to tell whose).
   */
  | {
      type: "guest.speech";
      guest_id?: string;
      label: string;
      text?: string;
      voiced_ms: number;
      score?: number;
      turn_id?: Id;
      /** 1.10, `speech_mine`: the ignored speech this is part of (`speech.mine`). */
      ignored_id?: string;
    }
  /** 1.8, `guests`: this session's guests, and the saved ones (names and sizes only). */
  | { type: "guest.list"; guests: GuestInfo[]; saved: SpeakerProfileInfo[] }
  /**
   * 1.10, `speech_mine`: the ignored speech `ignored_id` is now the user's ("That was me").
   * `delivered`: sent to the assistant as the turn `turn_id` (`late`: with a note that it was
   * said earlier). `learned`: added to their voiceprint; else `reason` says why not.
   */
  | {
      type: "speech.reattributed";
      ignored_id: string;
      mark_id: string;
      text?: string;
      delivered: boolean;
      late?: boolean;
      turn_id?: Id;
      learned: boolean;
      reason: string;
      score: number;
      threshold: number;
      floor: number;
      profile?: string;
      source?: string;
      guest_id?: string;
      guest_removed?: boolean;
      /**
       * Kit 0.12: not sent to the assistant (it was not the latest thing the user said, or too
       * old): theirs now, and it can still be sent (`speech.mine.send`).
       */
      sendable?: boolean;
    }
  /**
   * 1.10, kit 0.12: a marked line was sent to the assistant later (`speech.mine.send`). `late`:
   * with a note that it was said earlier; `before_turn_id` (1.12, kit 0.13): it was said before
   * that turn of the user's, which the assistant got (and answered) first.
   */
  | { type: "speech.mine.sent"; ignored_id: string; turn_id: Id; late?: boolean; source?: string; before_turn_id?: Id }
  /** 1.10, `speech_mine`: the "That was me" history, oldest first. */
  | { type: "speech.marks"; marks: MarkInfo[] }
  /**
   * 1.10, `speech_mine`: one chunk of an ignored stretch's audio (`speech.clip.get`), to the
   * asker only: PCM16 LE mono at `rate`, base64; complete when `offset + bytes == size`.
   */
  | { type: "speech.clip"; ignored_id: string; rate: number; size: number; offset: number; data: string }
  | {
      type: "reply.started";
      reply_id: Id;
      turn_id: Id | null;
      unprompted: boolean;
      canned: boolean;
      /** 1.3, `conversations` only. */
      conversation_id?: string;
    }
  | { type: "reply.text"; reply_id: Id; text: string; spoken: boolean }
  | { type: "tts.chunk"; reply_id: Id; seq: number; text: string; source: string | null; ms: number; words: Word[] }
  | { type: "reply.heard"; reply_id: Id; outcome: HeardOutcome; heard: string; spoken_so_far: string; cause?: string }
  | { type: "reply.ended"; reply_id: Id; outcome: ReplyOutcome; error?: string }
  /** 1.9, `pause`: the voiced reply was paused or resumed; `ms` of it had been heard. */
  | { type: "reply.paused"; reply_id: Id; paused: boolean; ms: number }
  | {
      type: "user.turn";
      turn_id: Id;
      reply_id: Id;
      text: string;
      interrupted?: { reply_id: Id; heard: string; spoken_so_far: string };
      note?: string;
      /** 1.3, `conversations` only. */
      conversation_id?: string;
      /** 1.5, `attachments` only: the files, with their bytes (base64). */
      attachments?: (AttachmentInfo & { data: string })[];
      /**
       * 1.12, kit 0.13: a "That was me" line: the ignored speech it was, when it was heard (Unix
       * ms) and, sent after a later turn of the user's, that turn (`before_turn_id`).
       */
      mine?: { ignored_id: string; heard_at_ms: number; before_turn_id?: Id };
    }
  | { type: "work.cancel"; reply_id: Id | null; reason: "command" | "phrase" }
  | { type: "push.accepted"; push_id: string; reply_id: Id }
  | { type: "ack"; re: string }
  | { type: "error"; code: string; message: string; re?: string; supported?: number[] }
  | { type: "pong" }
  /**
   * 1.11, `conversation_recent`: to a phone, right after `welcome` (PROTOCOL.md section 10): what the conversation said
   * recently, so a phone that was away (its leg closes when the screen locks) shows what it
   * missed. Plain events: `stt.final` per user turn, and per reply `reply.started`, its shown
   * text so far as one `reply.text` (`spoken: false`), `reply.ended` and `reply.heard`. Merge it
   * (`TranscriptModel` does): what is already shown is not repeated. Text only, never voiced.
   */
  | { type: "conversation.recent"; events: ServerMsg[] }
  // phone access (1.2, PROTOCOL.md section 10): to the PC's own clients, never phones
  | { type: "phone.status"; state: string; relay: string; desk_id?: string; prefix?: string; message?: string; legs: number }
  | { type: "phone.pair.code"; code: string; url: string; expires_s: number }
  | { type: "phone.pair.pending"; device_id: string; name: string; sas: string }
  | { type: "phone.devices"; devices: PhoneDevice[] };

export type ServerMsgType = ServerMsg["type"];
export type ServerMsgOf<T extends ServerMsgType> = Extract<ServerMsg, { type: T }>;

export type ClientMsg =
  | {
      type: "hello";
      protocol: number;
      role: "brain" | "ui";
      name?: string;
      events?: boolean;
      audio?: { mic: boolean; speaker: boolean };
      /** 1.3: the optional features this client uses. */
      features?: Feature[];
    }
  | {
      type: "session.start";
      audio?: SessionAudio;
      speaker_profile?: string;
      conversation_id?: string;
      mode?: SessionMode;
      turns?: DictationTurns;
    }
  | { type: "dictation.start" }
  | { type: "dictation.stop" }
  | { type: "session.conversation"; conversation_id: string | null }
  | { type: "session.stop"; reason?: string }
  | { type: "user.text"; text: string; conversation_id?: string; attachments?: string[] }
  // 1.5, feature `attachments`: uploads in ordered chunks, then sent with a turn
  | {
      type: "attachment.put";
      attachment_id: string;
      name: string;
      mime: string;
      size: number;
      offset: number;
      /** This chunk, base64. */
      data: string;
      thumb?: string;
    }
  | { type: "attachment.stage"; attachment_ids: string[] }
  | { type: "reply.silence"; reply_id?: Id }
  // 1.9, feature `pause`
  | { type: "reply.pause"; reply_id?: Id }
  | { type: "reply.resume"; reply_id?: Id }
  | { type: "work.cancel"; reply_id?: Id }
  | { type: "playback.progress"; reply_id: Id; ms: number }
  | { type: "playback.done"; reply_id: Id; ms: number }
  | { type: "ping" }
  | { type: "speaker.enroll.start"; profile: string; source?: string }
  | { type: "speaker.enroll.stop"; save?: boolean }
  | { type: "speaker.verify"; enabled: boolean }
  | { type: "speaker.forget"; profile: string; source?: string }
  | { type: "speaker.status" }
  // 1.8, feature `guests`
  | { type: "guest.rename"; guest_id: string; name?: string; save?: boolean }
  | { type: "guest.forget"; guest_id?: string; name?: string }
  | { type: "guests.get" }
  // 1.10, feature `speech_mine`
  | { type: "speech.mine"; ignored_id: string }
  | { type: "speech.mine.send"; ignored_id: string }
  | { type: "speech.unlearn"; mark_id: string }
  | { type: "speech.marks.get" }
  | { type: "speech.clip.get"; ignored_id: string }
  | { type: "speech.clip.playing"; ignored_id: string; playing: boolean }
  // Diagnostics for the host's log (a host extension, not in voice-kit-protocol: kits that
  // don't know it answer `unknown_type`, which the sender ignores).
  | { type: "client.log"; event: string; data?: Record<string, unknown> }
  // phone access (1.2): never from a phone
  | { type: "phone.status" }
  | { type: "phone.pair.start" }
  | { type: "phone.pair.cancel" }
  | { type: "phone.devices" }
  | { type: "phone.confirm"; device_id: string }
  | { type: "phone.revoke"; device_id: string }
  | { type: "phone.forget"; device_id: string }
  // brain only
  | { type: "reply.delta"; reply_id: Id; text: string; show?: boolean; speak?: boolean }
  | { type: "reply.end"; reply_id: Id; error?: string; cancelled?: boolean }
  | {
      type: "push";
      push_id: string;
      text: string;
      end?: boolean;
      priority?: "when_idle" | "idle" | "now";
      conversation_id?: string;
    };

/** A client message plus the optional correlation `id` (echoed as `re` in `ack` / `error`). */
export type ClientFrame = ClientMsg & { id?: string };

/** Parses one text frame. Unknown types pass through (receivers ignore what they don't know). */
export function parseServerMsg(data: string): ServerMsg | null {
  try {
    const v = JSON.parse(data);
    return v && typeof v === "object" && typeof v.type === "string" ? (v as ServerMsg) : null;
  } catch {
    return null;
  }
}

/** Down audio frame: `reply_id` as u64 LE, then PCM16 LE mono at 24 kHz. */
export function decodeAudioDown(frame: ArrayBuffer): { replyId: Id; pcm: Int16Array } | null {
  if (frame.byteLength < AUDIO_HEADER_BYTES) return null;
  const view = new DataView(frame);
  const replyId = view.getUint32(0, true) + view.getUint32(4, true) * 2 ** 32;
  const samples = (frame.byteLength - AUDIO_HEADER_BYTES) >> 1;
  const pcm = new Int16Array(samples);
  for (let i = 0; i < samples; i++) pcm[i] = view.getInt16(AUDIO_HEADER_BYTES + i * 2, true);
  return { replyId, pcm };
}

export function encodeAudioDown(replyId: Id, pcm: Int16Array): ArrayBuffer {
  const buf = new ArrayBuffer(AUDIO_HEADER_BYTES + pcm.length * 2);
  const view = new DataView(buf);
  view.setUint32(0, replyId % 2 ** 32, true);
  view.setUint32(4, Math.floor(replyId / 2 ** 32), true);
  for (let i = 0; i < pcm.length; i++) view.setInt16(AUDIO_HEADER_BYTES + i * 2, pcm[i], true);
  return buf;
}

/** Up audio: PCM16 LE bytes (a copy, so the caller may reuse its buffer). */
export function pcm16Bytes(pcm: Int16Array): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(pcm.length * 2);
  const view = new DataView(out.buffer);
  for (let i = 0; i < pcm.length; i++) view.setInt16(i * 2, pcm[i], true);
  return out;
}

export function int16ToFloat32(pcm: Int16Array): Float32Array<ArrayBuffer> {
  const out = new Float32Array(pcm.length);
  for (let i = 0; i < pcm.length; i++) out[i] = pcm[i] / 32768;
  return out;
}
